import {useState, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

// SELECTORS
import {hotelNameSelector} from '@stencil/hotels-cg-reducer/src/details/selectors';
import {countrySelectorFn} from '@stencil/hotels-common-reducer/src/selectors';

// UTILS
import {ratingBreakupCalculator} from '@stencil/hotels-utils/src/details/ratingBreakupCalculator';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {
  TOTAL_REVIEWS_PER_PAGE,
  checkIsExternalRating,
} from '@stencil/hotels-utils/src/common-utils';

// CONFIG
import {omnitureConfig, omnitureThunk, omnitureUtils} from '@stencil/omniture-analytics';
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';

// HOOKS
import useFetchGiOtaReviewsV2 from '@stencil/hotels-hooks/src/useFetchGiOtaReviewsV2';

// COMPONENTS
import GuestRating from '../../GuestRating/GuestRating';
import RatingsBreakup from '../../RatingsBreakup/RatingsBreakup';
import UserReview from '../UserReview/UserReview';
import Pagination from '../../Pagination/Pagination';
import DropDownCheckList from '../../../SRP/DropDownCheckList/DropDownCheckList';
import WhatOurGuestSays from '../WhatOurGuestSays/WhatOurGuestSays';
import OtaReview from '../../OtaReview/OtaReview';
import ExternalRatingsBreakup from '../../ExternalRatingBreakup/ExternalRatingBreakup';
import TravelImpression from '@stencil/gi-components/src/TravelImpression/TravelImpression';

// ICONS
import HappyInfoOutlinedIcon from '@stencil/icons/src/HappyInfoOutlinedIcon/HappyInfoOutlinedIcon';

// STYLES
import {LargeSectionHeader, FlexItem} from '../../../../CommonStyleUtils/dwebCommon.styles';
import {
  ContentWrapperDiv,
  OuterWrapperDiv,
  HeadingWrapperDiv,
  EGRHeaderDesc,
  HiddenTooltipWrapperDiv,
  InfoIconWrapper,
  TipPositioning,
  ReviewByUserWrapperDiv,
  UserReviewOuterWrapperDiv,
  FlexContainerWrap,
} from './ReviewNRating.styles';

const ReviewNRating = ({
  defaultSortOption,
  ugcSummary,
  sortingList,
  seekTagDetails,
  travellerImpressions,
}) => {
  const dispatch = useDispatch();

  const [status, updateStatus] = useState(['default']);
  const [selectedSubConcepts, setSelectedSubConcepts] = useState([]);
  const [sortBy, setSortBy] = useState(defaultSortOption || sortingList?.[0]?.criteriaType);

  const propertyNameOriginal = useSelector(hotelNameSelector);
  const countryCode = useSelector(countrySelectorFn);

  const {
    source,
    ratingBreakup,
    ratingCount,
    reviewCount,
    cumulativeRating,
    subConcepts,
    availableOTAs,
    extRating,
    ratingSummaryGI,
  } = ugcSummary ?? {};

  const isInternalNational = countryCode !== 'IN';
  const isExternalRating = checkIsExternalRating(source);
  const propertyName =
    propertyNameOriginal?.length > 71
      ? `${propertyNameOriginal.substring(0, 68)}...`
      : propertyNameOriginal;

  const ratingWithCount = ratingBreakupCalculator(ratingBreakup);

  const pushPaginationGA = pageNumber => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.RatingAndReviewsV2}_${detailsClick.rnr.paginationClicked}_${pageNumber}_number_clicked`,
          type: omnitureConfig.ctaType.Hyperlink,
          componentName: DetailsPageComponents.RatingAndReviewsV2,
          destination: '',
        },
      }) as any,
    );
  };

  const handleSubConceptSelection = useCallback(
    tag => {
      if (selectedSubConcepts.includes(tag?.subConcept)) {
        setSelectedSubConcepts(selectedSubConcepts.filter(item => item !== tag?.subConcept));
      } else {
        setSelectedSubConcepts([...selectedSubConcepts, tag?.subConcept]);
      }
    },
    [selectedSubConcepts],
  );

  const handleSortSelection = useCallback(
    sort => {
      setSortBy(sort);
      updateStatus(status.concat(sort));
      dispatch(
        omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.sortFunction, {
          filter: {sort},
        }) as any,
      );
    },
    [sortBy],
  );

  const getPageVolume = () => TOTAL_REVIEWS_PER_PAGE;

  const onExpediaReviewsLoaded = () => {
    omnitureUtils.setLoadedComponents('Exp_reviews_loaded');
  };

  const {totalReviewsCount, onChangePage, reviews, page} = useFetchGiOtaReviewsV2({
    scrollId: detailsNavigationIdsAndValues.guestReviews.id,
    flavour: 'desktop',
    subConcepts: selectedSubConcepts,
    sortBy,
    onExpediaReviewsLoaded: onExpediaReviewsLoaded,
  });

  useEffect(() => {
    omnitureUtils.setLoadedComponents(`preferred_src_${source}_loaded`);
  }, []);

  if (!ugcSummary) return null;

  return (
    <ContentWrapperDiv>
      <OuterWrapperDiv>
        <HeadingWrapperDiv isExternal={isExternalRating}>
          <LargeSectionHeader>
            {isExternalRating ? (
              extRating?.descriptionTitle
            ) : (
              <>Guest Reviews & Rating {propertyName && `for ${propertyName}`}</>
            )}
          </LargeSectionHeader>
          {isExternalRating && (
            <EGRHeaderDesc>
              ({extRating?.header})
              <InfoIconWrapper>
                <HappyInfoOutlinedIcon />
                <HiddenTooltipWrapperDiv>
                  <TipPositioning />
                  <p>{extRating?.descriptionInfo}</p>
                </HiddenTooltipWrapperDiv>
              </InfoIconWrapper>
            </EGRHeaderDesc>
          )}
          {source === 'GI' && (
            <FlexItem>
              <DropDownCheckList
                options={sortingList}
                title="Sort By"
                selOption={sortBy}
                selOptionName={sortBy}
                keyNameForOption="criteriaType"
                keyNameForOptionName="displayText"
                dataTestIdForTitle="displayText"
                dataTestIdForOptions="criteriaType"
                onHandleChange={handleSortSelection}
              />
            </FlexItem>
          )}
        </HeadingWrapperDiv>

        {isExternalRating ? (
          <ExternalRatingsBreakup
            extRating={ratingSummaryGI}
            hotelRating={cumulativeRating}
            ratingCount={ratingCount}
          />
        ) : (
          <ReviewByUserWrapperDiv>
            <FlexContainerWrap>
              {source === 'EXP' || isInternalNational ? (
                <OtaReview
                  hotelRating={cumulativeRating}
                  ratingCount={ratingCount}
                  reviewCount={reviewCount}
                />
              ) : (
                <>
                  <GuestRating
                    availableOTAs={availableOTAs}
                    hotelRating={cumulativeRating}
                    ratingCount={ratingCount}
                    reviewCount={reviewCount}
                    source={source}
                  />
                  <RatingsBreakup rwc={ratingWithCount} />
                </>
              )}
            </FlexContainerWrap>
            <FlexContainerWrap>
              {subConcepts?.length > 0 && (
                <WhatOurGuestSays
                  tagData={subConcepts}
                  selectedTags={selectedSubConcepts}
                  onSelectTags={setSelectedSubConcepts}
                />
              )}
            </FlexContainerWrap>
          </ReviewByUserWrapperDiv>
        )}
        {travellerImpressions && <TravelImpression seekTagDetails={seekTagDetails} isDesktop />}
        <UserReviewOuterWrapperDiv>
          {reviews?.length ? (
            reviews.map(item => <UserReview reviewData={item} key={item.id} />)
          ) : (
            <div id="dontHaveData" />
          )}
        </UserReviewOuterWrapperDiv>
        {reviews?.length > 0 && (
          <Pagination
            onChangePage={onChangePage}
            initialPage={page || 1}
            getPageSize={getPageVolume}
            totalNumberOfItems={totalReviewsCount}
            initCall={false}
            maxPages={6}
            pushPaginationGA={pushPaginationGA}
            linkable={true}
            status={status}
          />
        )}
      </OuterWrapperDiv>
    </ContentWrapperDiv>
  );
};

export default ReviewNRating;
